import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { ArrowLeft } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

import { useWizardNavigation } from '@subscriptions/presentation/components/base/SubscriptionBlockWizardLayout';

type WizardStepBackButtonProps = {
  className?: string;
};

/**
 * Icon-only back button for wizard step footers (desktop only, `hidden md:inline-flex`).
 *
 * Consumes `onBack` from the wizard navigation context published by
 * `SubscriptionBlockWizardLayout`; renders nothing when the active step has no back
 * navigation. On mobile the layout's top-left back button (aligned with the X) takes
 * over, so only one back button is ever visible per breakpoint.
 *
 * @param {WizardStepBackButtonProps} props - Component properties
 * @param {string} [props.className] - Extra classes merged into the button (e.g. `mr-auto` when embedded in a form footer)
 */
export const WizardStepBackButton = ({ className }: WizardStepBackButtonProps) => {
  const { t } = useTranslation('subscriptions');
  const { onBack } = useWizardNavigation();

  if (!onBack) return null;

  return (
    <Button
      aria-label={t('blockCreationWizard.backAriaLabel')}
      className={cn('hidden md:inline-flex', className)}
      onClick={onBack}
      size="icon"
      type="button"
      variant="outline"
    >
      <ArrowLeft className="h-4 w-4" />
    </Button>
  );
};
